import { Component, Inject, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ChatService } from './chat.service';
import { ChatMessage } from './chat-message.model';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { WebsocketService } from '../../navbar/notification/websocket.service';
import { ServiceOrderService } from '../../../views/registration/service-orders/crud-service';
import { serviceOrderTypeEnum } from '../../../domain/registration/enums/serviceOrder-type.enum';

@Component({
  selector: 'app-chat-widget',
  templateUrl: './chat-widget.component.html',
  styleUrls: ['./chat-widget.component.sass'],
})
export class ChatWidgetComponent implements OnInit, AfterViewInit, OnDestroy {
  messages: ChatMessage[] = [];
  newMessage: string = '';
  currentUserId: string = '';
  error: string | null = null;
  serviceOrderId: string;
  loading: boolean = false;
  chatMessageSub?: Subscription;
  private isInitialLoad: boolean = true;
  serviceOrderData: any = null;
  isDescriptionExpanded: boolean = false;
  isSendingMessage: boolean = false;

  @ViewChild('chatBody') chatBody!: ElementRef;
  @ViewChild('scrollToBottom') scrollToBottomElement!: ElementRef;

  constructor(
    private chatService: ChatService,
    private authService: AuthService,
    private websocketService: WebsocketService,
    private serviceOrderService: ServiceOrderService,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ChatWidgetComponent>
  ) {}

  ngOnInit(): void {
    this.serviceOrderId = this.data.serviceOrderId;
    this.currentUserId = this.authService.getUserId();
    this.loadMessages();
    
    // Entrar na sala de chat
    this.websocketService.joinChatRoom(this.serviceOrderId);
    
    // Escutar novas mensagens de chat
    this.chatMessageSub = this.websocketService.listenForChatMessages().subscribe(
      (newMessage: ChatMessage) => {
        // Só adiciona se for do chat atual
        if (newMessage.serviceOrderId === this.serviceOrderId) {
          this.messages.push(newMessage);
          // Força detecção de mudanças e scroll para nova mensagem
          this.cdr.detectChanges();
          setTimeout(() => {
            this.scrollToBottomImmediately();
          }, 50);
        }
      }
    );
  }

  ngAfterViewInit(): void {
    // Aguarda um pouco para garantir que o DOM foi renderizado
    setTimeout(() => {
      this.scrollToBottomImmediately();
    }, 100);
  }

  ngOnDestroy(): void {
    this.chatMessageSub?.unsubscribe();
    // Sair da sala de chat
    this.websocketService.leaveChatRoom(this.serviceOrderId);
  }

  loadMessages(showLoading: boolean = true): void {
    if (showLoading) this.loading = true;
    
    // Primeiro carrega os dados da ordem de serviço
    this.serviceOrderService.getServiceOrderDetails(this.serviceOrderId).subscribe({
      next: (serviceOrder) => {
        console.log('[Chat] Dados completos da ordem de serviço:', serviceOrder);
        this.serviceOrderData = serviceOrder;
        
        // Log para debug dos campos de descrição
        console.log('[Chat] Campos de descrição:', {
          fileDescription: serviceOrder.fileDescription,
          description: serviceOrder.description,
          reason: serviceOrder.reason,
          name: serviceOrder.name,
          response: serviceOrder.response,
          title: serviceOrder.title
        });
        
        // Log de todos os campos disponíveis
        console.log('[Chat] Todos os campos disponíveis:', Object.keys(serviceOrder));
        
        // Depois carrega as mensagens do chat (sem incluir a descrição da OS)
        this.chatService.getMessages(this.serviceOrderId).subscribe({
          next: (messages) => {
            // Não inclui mais a mensagem de descrição no corpo do chat
            this.messages = messages;
            
            this.loading = false;
            this.isInitialLoad = true;
            
            // Aguarda a renderização e vai para a última mensagem
            setTimeout(() => {
              this.scrollToBottomImmediately();
              this.isInitialLoad = false;
            }, 200);
          },
          error: (err) => {
            this.loading = false;
            this.error = 'Erro ao carregar mensagens.';
          },
        });
      },
      error: (err) => {
        console.error('[Chat] Erro ao carregar dados da ordem de serviço:', err);
        this.loading = false;
        this.error = 'Erro ao carregar dados da ordem de serviço.';
      },
    });
  }

  /**
   * Scroll suave para o final do chat
   */
  scrollToBottom(): void {
    if (this.chatBody && this.chatBody.nativeElement) {
      try {
        const chatBodyElement = this.chatBody.nativeElement;
        chatBodyElement.scrollTo({
          top: chatBodyElement.scrollHeight,
          behavior: 'smooth'
        });
      } catch (err) {
        console.warn('Erro ao fazer scroll suave:', err);
        this.scrollToBottomImmediately();
      }
    }
  }

  /**
   * Scroll imediato para o final do chat (sem animação)
   */
  scrollToBottomImmediately(): void {
    if (this.chatBody && this.chatBody.nativeElement) {
      try {
        const chatBodyElement = this.chatBody.nativeElement;
        chatBodyElement.scrollTop = chatBodyElement.scrollHeight;
        
        // Log para debug
        console.log(`[Chat] Scroll realizado para posição: ${chatBodyElement.scrollTop}/${chatBodyElement.scrollHeight}`);
      } catch (err) {
        console.warn('Erro ao fazer scroll imediato:', err);
      }
    } else {
      console.warn('[Chat] Elemento chatBody não encontrado para scroll');
    }
  }

  /**
   * Scroll para uma mensagem específica
   */
  scrollToMessage(messageId: string): void {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement && this.chatBody) {
      try {
        messageElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      } catch (err) {
        console.warn('Erro ao scroll para mensagem:', err);
      }
    }
  }

  checkAndLoadChat(): void {
    this.loadMessages();
  }

  startNewChat(): void {
    const initialMessage: ChatMessage = {
      id: '',
      serviceOrderId: this.serviceOrderId,
      userId: this.currentUserId,
      partnerName: '',
      message: 'Chat iniciado.',
      timestamp: new Date(),
      isBot: false,
    };
    this.chatService.sendMessage(this.serviceOrderId, initialMessage).subscribe({
      next: (message) => {
        this.messages.push(message);
        this.scrollToBottomImmediately();
        this.error = null;
      },
      error: (err) => {
        this.error = 'Erro ao iniciar chat.';
      },
    });
  }

  getMessageClass(message: ChatMessage): string {
    return message.userId === this.currentUserId ? 'message-right' : 'message-left';
  }

  formatTimestamp(timestamp: Date): string {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  formatMessageContent(message: string): string {
    // Converte **texto** para <strong>texto</strong> para formatação em negrito
    return message.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  }

  /**
   * Obtém a descrição da ordem de serviço
   */
  public getServiceOrderDescription(): string {
    if (!this.serviceOrderData) {
      console.log('[Chat] serviceOrderData não disponível');
      return '';
    }

    // Prioridade dos campos de descrição
    const descriptionFields = [
      this.serviceOrderData.fileDescription,
      this.serviceOrderData.description,
      this.serviceOrderData.reason,
      this.serviceOrderData.response,
      this.serviceOrderData.title,
      this.serviceOrderData.name,
      this.serviceOrderData.comments,
      this.serviceOrderData.observations,
      this.serviceOrderData.details
    ];

    console.log('[Chat] Campos de descrição verificados:', descriptionFields);

    // Retorna o primeiro campo que tenha conteúdo
    for (const field of descriptionFields) {
      if (field && typeof field === 'string' && field.trim() !== '') {
        console.log('[Chat] Descrição encontrada:', field.trim());
        return field.trim();
      }
    }

    console.log('[Chat] Nenhuma descrição encontrada');
    return '';
  }

  /**
   * Obtém a descrição do tipo de ordem de serviço
   */
  public getServiceOrderTypeDescription(fileType: any): string {
    if (!fileType) return 'N/A';
    
    // O fileType já é uma string com os valores do enum
    if (typeof fileType === 'string') {
      return fileType;
    }
    
    // Fallback para números (caso raro)
    if (typeof fileType === 'number') {
      const typeValues = Object.values(serviceOrderTypeEnum);
      return typeValues[fileType - 1] || `Tipo ${fileType}`;
    }
    
    return 'N/A';
  }

  getUserName(message: ChatMessage): string {
    if (message.userId === this.currentUserId) {
      return this.authService.getUserName();
    }
    return message.senderName || message.userName || 'Usuário';
  }

  sendMessage(): void {
    // Previne envios múltiplos
    if (this.isSendingMessage) {
      return;
    }

    if (!this.newMessage.trim()) {
      this.error = 'A mensagem não pode estar vazia.';
      return;
    }
    
    this.isSendingMessage = true;
    
    const message: ChatMessage = {
      serviceOrderId: this.serviceOrderId,
      userId: this.currentUserId,
      message: this.newMessage,
      timestamp: new Date(),
      isBot: false,
    };
    
    this.chatService.sendMessage(this.serviceOrderId, message).subscribe({
      next: (sentMessage) => {
        this.messages.push(sentMessage);
        this.newMessage = '';
        this.error = null;
        this.isSendingMessage = false;
        // Scroll imediato para a nova mensagem enviada
        this.scrollToBottomImmediately();
      },
      error: (err) => {
        this.error = 'Erro ao enviar mensagem.';
        this.isSendingMessage = false;
      },
    });
  }

  toggleDescription(): void {
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
  }

  trackByMessageId(index: number, message: ChatMessage): string {
    return message.id || index.toString();
  }
}
